import { DOCUMENT } from '@angular/common';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CustomerFeaturesService, UtilsService } from '@dxc/data-access';
import { environment } from '@dxc/environments';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NavItem } from '@shared/types';
import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental';
import { NgxSonnerToaster } from 'ngx-sonner';
import { IStaticMethods } from 'preline/preline';
import { filter } from 'rxjs';

// Declares global interface to extend the Window object for custom static methods
declare global {
  interface Window {
    HSStaticMethods: IStaticMethods;
  }
}

/**
 * Root component of the application, responsible for rendering the main layout,
 * handling theme changes, and reacting to route changes.
 * It utilizes Angular's standalone component feature and Signals for reactive state management.
 */
@UntilDestroy()
@Component({
  imports: [RouterModule, AngularQueryDevtools, NgxSonnerToaster],
  selector: 'dxc-root',
  template: `
    <router-outlet></router-outlet>

    @if (!environment.production) {
      <angular-query-devtools [initialIsOpen]="false" />
    }

    <ngx-sonner-toaster
      closeButton
      duration="5000"
      richColors
      [theme]="isLightTheme() ? 'light' : 'dark'"
    />
  `
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  protected customerFeaturesService = inject(CustomerFeaturesService);
  private document = inject(DOCUMENT);
  private utilsService = inject(UtilsService);
  protected environment = environment;
  protected isLightTheme = signal(this.getTheme());
  private currentRouteName = signal('');

  constructor() {
    // Use effect for applying theme changes reactively
    effect(() => {
      this.applyTheme(this.isLightTheme());
    });
  }

  ngOnInit() {
    // console.log(`🚀 ~ environment.customer:`, environment.customer);
    // console.log(`🚀 ~ environment.branch:`, environment.branch);
    // Use RxJS pipe and filter for type-safe event handling
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        this.utilsService.routeHistory.set([
          ...this.utilsService.routeHistory(),
          event.urlAfterRedirects
        ]);
        this.setCurrentRouteName();
        setTimeout(() => {
          // window.HSStaticMethods.autoInit();
        }, 100);
      });
  }

  /**
   * Dynamically determines the current route name based on the URL and updates the header title.
   * This method dynamically imports `SidebarNavItems` to reduce the initial bundle size and then
   * iterates through these items to find a match with the current URL.
   */
  private async setCurrentRouteName(): Promise<void> {
    const currentUrl = this.router.url;
    let newRouteName = '';

    const { SidebarNavItems } = await import('@shared/layouts');

    const findMatchingItem = (items: NavItem[]): NavItem | undefined => {
      for (const item of items) {
        if (
          item.routerLink
          && currentUrl.startsWith(item.routerLink)
          && item.routerLink !== '/'
        ) {
          return item;
        }
        if (item.subItems) {
          const subItem = findMatchingItem(item.subItems);
          if (subItem) {
            return subItem;
          }
        }
      }
      return undefined;
    };

    const matchingItem = findMatchingItem(SidebarNavItems);

    if (matchingItem) {
      newRouteName = matchingItem.name;
    } else if (currentUrl === '/') {
      // Handle root route
      newRouteName = this.customerFeaturesService.hasFeature(
        'moduleHomeAbrdnTrades'
      )
        ? 'Trades'
        : 'My Cases';
    }

    // Special case for 'cases/trade'
    if (currentUrl.includes('cases/trade')) {
      newRouteName = 'Trade';
    }

    // Update signals with the new route name
    this.currentRouteName.set(newRouteName);
    this.utilsService.headerTitleSignal.set(newRouteName);
  }

  /**
   * Retrieves the user's theme preference from local storage.
   * @returns {boolean} True if the theme is light, false if dark.
   */
  private getTheme(): boolean {
    return localStorage.getItem('theme') !== 'dark';
  }

  /**
   * Applies the current theme by setting a class on the document body.
   * Also updates the theme preference in local storage.
   */
  private applyTheme(isLight: boolean) {
    const themeClass = isLight ? 'light' : 'dark';
    this.document.body.className = themeClass;
    localStorage.setItem('theme', themeClass);
  }
}
